'use strict';

import _ from 'lodash';

import './LegacyPaypalButton.less';
import { STRIPE_DURATION } from 'ck-core/source/data/StripeDuration';
import { FACEBOOK_CUSTOM_EVENT, trackFacebookPixel } from 'ck-core-client/source/trackers/Facebook';
import { SerialPromiseQueue } from 'ck-core/source/classes/SerialPromiseQueue';

const Language = require('ck-core/source/services/Language');
const subscriptionPlans = require('ck-core/source/collections/subscriptionPlans');
const CSSUtils = require('ck-core-client/source/utils/CSSUtils');
const Component = require('ck-core-client/source/ui/Component');
const LegacyPaypalButtonLang = require('./LegacyPaypalButtonLang');

module.exports = Component.define({
	type: 'PaypalButton',

	init_ui: function(paymentData, options) {
		this.options = _.defaultsDeep(options || {}, {
			setup: {}
		});

		if (typeof $ !== 'undefined') {
			this.dom = $('<div class="paypalButton" id="paypalButton">');

			this._mutationQueue = new SerialPromiseQueue();

			this.refresh(paymentData);

			this.dom.on(
				'tap',
				_.once(() => {
					if (!this._disabled) {
						trackFacebookPixel(FACEBOOK_CUSTOM_EVENT.ADD_CARD_DETAILS);
					}
				})
			);

			if (CK.mobile) {
				this.dom.on('tap', () => {
					if (!this._disabled) {
						this.braintreeInstance && this.braintreeInstance.paypal.initAuthFlow();
					}
				});
			}
		} else {
			// TEST
			this.dom = [];
		}
	},

	cleanup: function() {
		this._mutationQueue.queue('braintreeMutation', () => {
			return async function() {
				if (this.braintreeInstance) {
					await Promise.promisify(this.braintreeInstance.teardown)();
					this.braintreeInstance = null;
				}

				this.Component_cleanup();
			}.bind(this)();
		});
	},

	refresh: function(paymentData) {
		if (!this.dom) {
			return;
		}

		return this._mutationQueue.queue('braintreeMutation', () => {
			if (paymentData) {
				this._paymentData = _.cloneDeep(paymentData);
			}

			return async function() {
				if (!this._braintreeClientToken) {
					this._braintreeClientToken = await CK.clientSockets.users.emit(
						'generateBraintreeClientToken'
					);
				} else {
					await Promise.promisify(this.braintreeInstance.teardown)();
					this.braintreeInstance = null;
				}

				this.dom.empty();

				if (this._disabled) {
					this.disable();
				}

				var billingMessage;

				if (this.options.customBillingMessage) {
					billingMessage = this.options.customBillingMessage;
				} else {
					if (this._paymentData.planId) {
						let plan = subscriptionPlans(this._paymentData.planId);
						let coupon = this._paymentData.coupon;

						const price = plan.priceInCurrency(this._paymentData.currency, {
							coupon,
							spaceCurrencySymbol: false,
							user: CK.user
						});

						const hasCoupon = coupon && coupon.id() && coupon.active();

						var trialLang = hasCoupon
							? LegacyPaypalButtonLang.BILLING_TRIAL_COUPON
							: LegacyPaypalButtonLang.BILLING_TRIAL;

						var messageLang = plan.hasTrial()
							? trialLang
							: hasCoupon
								? LegacyPaypalButtonLang.BILLING_WITH_COUPON
								: LegacyPaypalButtonLang.BILLING;

						var period;

						if (plan.oneOff()) {
							messageLang = LegacyPaypalButtonLang.BILLING_ONE_OFF;
						} else {
							period =
								' ' +
								Language.mapVariables(
									plan.intervalCount() > 1
										? LegacyPaypalButtonLang.BILLING_PERIOD_MULTIPLE
										: LegacyPaypalButtonLang.BILLING_PERIOD_ONE,
									{
										interval: plan.interval(),
										intervalCount: plan.intervalCount()
									}
								);
						}

						let monthLang,
							couponDurationLang = '';

						if (hasCoupon) {
							monthLang = coupon.getDurationMessage();

							if (coupon.duration() === STRIPE_DURATION.REPEATING) {
								monthLang = 'for ' + monthLang;
							}

							couponDurationLang =
								coupon.duration() === STRIPE_DURATION.FOREVER
									? period
									: LegacyPaypalButtonLang.BILLING_COUPON;

							couponDurationLang = Language.mapVariables(couponDurationLang, {
								fullPrice: price.formattedFullPrice,
								monthLang
							});
						}

						let trialDays = plan.freeTrialDays();

						billingMessage = Language.mapVariables(messageLang, {
							discount: price.formattedDiscount,
							price: price.formatted,
							couponDuration: couponDurationLang,
							period,
							trialDays
						});
					} else {
						billingMessage = LegacyPaypalButtonLang.BILLING_CHANGE;
					}
				}

				CK.logger.debug('PayPal billing message', billingMessage);

				// HTML entity decode
				billingMessage = $('<textarea>')
					.html(billingMessage)
					.text();

				const braintree = (await import(/* webpackPreload: true */ 'braintree-web')).default;

				await new Promise(fulfil => {
					braintree.setup(
						this._braintreeClientToken,
						'custom',
						_.extend(
							{
								paypal: {
									container: this.dom,
									singleUse: false,
									billingAgreementDescription: billingMessage,
									onAuthorizationDismissed: function() {
										CK.trackAnalytic('PaypalAuthorisationDismissed');
									},
									onUnsupported: function() {
										CK.trackAnalytic('PaypalUnsupported');
									}
								},
								dataCollector: {
									paypal: true
								},
								onReady: braintreeInstance => {
									this.braintreeInstance = braintreeInstance;

									fulfil();
								}
							},
							this.options.setup
						)
					);
				});
			}.bind(this)();
		});
	},

	disable: function() {
		this._disabled = true;

		if (this.dom) {
			this.dom.addClass('pending');

			CSSUtils.addDisabledOverlay(this.dom);
		}
	},

	enable: function() {
		this._disabled = false;

		if (this.dom) {
			this.dom.removeClass('pending');

			CSSUtils.removeDisabledOverlay(this.dom);
		}
	},

	waitUntilReady: function() {
		return CK.fn.poll(() => !!this.braintreeInstance, {
			timeout: 10000
		});
	}
});
